/**
 * Meta (Facebook) Pixel helper — Pixel ID 1712418656849899.
 *
 * Design goals (from product spec):
 *   1. Every event fires exactly once for a given user action
 *      (no duplicates from React re-renders, rapid clicks, route changes).
 *   2. Tracking failures NEVER break the shopping experience.
 *      All fbq calls are wrapped in try/catch and no-op when fbq is missing.
 *   3. Dev-only console logging: enabled when NODE_ENV !== 'production'.
 *      Webpack/CRA injects NODE_ENV at build time, so this is statically
 *      dead-code-eliminated from prod bundles.
 *   4. Standard Meta event names + parameters per
 *      https://developers.facebook.com/docs/meta-pixel/reference#standard-events
 *
 * The actual pixel <script> + init('1712418656849899') + initial PageView
 * lives in /public/index.html — this module only emits subsequent events.
 */

const CURRENCY = 'USD';
const IS_DEV = process.env.NODE_ENV !== 'production';

// -------- internal helpers --------

const fire = (eventName, params, options) => {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      // fbq signature: fbq('track', name, params, { eventID })
      if (options && options.eventID) {
        window.fbq('track', eventName, params || {}, { eventID: options.eventID });
      } else if (params) {
        window.fbq('track', eventName, params);
      } else {
        window.fbq('track', eventName);
      }
    }
  } catch (_) {
    /* swallow — analytics must never throw to the UI */
  }
  if (IS_DEV) {
    console.log(`Meta Pixel: ${eventName}`, params || '', options || '');
  }
};

/**
 * Dedup registry. Keys are arbitrary strings ("event:productId" etc.).
 * `windowMs` controls how long a key is considered "seen" — short for
 * click-debounce (AddToCart), longer for page-scoped events.
 */
const seen = new Map(); // key -> timestamp
const shouldEmit = (key, windowMs) => {
  const now = Date.now();
  const last = seen.get(key);
  if (last && now - last < windowMs) return false;
  seen.set(key, now);
  return true;
};

// -------- public API --------

/**
 * Fires on route changes (initial PageView is fired by the inline pixel
 * snippet in index.html). Deduped per pathname within a 100ms window so
 * StrictMode double-renders don't double-count.
 */
export const trackPageView = (pathname = '') => {
  if (!shouldEmit(`pv:${pathname}`, 100)) return;
  fire('PageView');
};

/**
 * Fire when a product page (or modal) becomes the active view.
 * Deduped per productId so opening the same product twice in a session
 * counts twice (different "views"), but a single open won't fire twice
 * due to React effect re-runs.
 */
export const trackViewContent = ({ id, name, price } = {}) => {
  if (!id) return;
  // Short window: dedup against accidental double-fire from effects/StrictMode.
  // Reopening the same product after navigation is a legitimate new ViewContent.
  if (!shouldEmit(`vc:${id}`, 1500)) return;
  fire('ViewContent', {
    content_ids: [String(id)],
    content_name: name,
    content_type: 'product',
    value: Number(price) || 0,
    currency: CURRENCY,
  });
};

/**
 * Fire AFTER a product is successfully added to the cart (not just on click).
 * Deduped per productId within 1s to catch rapid double-clicks.
 */
export const trackAddToCart = ({ id, name, price, quantity = 1 } = {}) => {
  if (!id) return;
  if (!shouldEmit(`atc:${id}:${quantity}`, 1000)) return;
  fire('AddToCart', {
    content_ids: [String(id)],
    content_name: name,
    content_type: 'product',
    contents: [{ id: String(id), quantity }],
    value: Number(price) * quantity,
    currency: CURRENCY,
  });
};

/**
 * Fire when the user truly begins checkout (the Stripe redirect button click,
 * after validation). Deduped against accidental double-fire from re-renders.
 */
export const trackInitiateCheckout = ({ cartTotal, numItems, contentIds = [] } = {}) => {
  // Use a value+itemCount signature so a legitimate retry after cart changes
  // still tracks, but a double-click within the same total/items will not.
  const key = `ic:${(Number(cartTotal) || 0).toFixed(2)}:${numItems}`;
  if (!shouldEmit(key, 5000)) return;
  fire('InitiateCheckout', {
    value: Number(cartTotal) || 0,
    num_items: numItems,
    content_ids: contentIds.map(String),
    content_type: 'product',
    currency: CURRENCY,
  });
};

/**
 * Fire when a high-intent lead form is submitted (bulk order quote request,
 * custom-design request). These are top-of-funnel conversions Meta can
 * optimize ads against. Deduped per form-content-name within 10s to catch
 * accidental double-submissions.
 */
export const trackLead = ({ contentName, value = 0, eventID } = {}) => {
  const key = `ld:${contentName || 'lead'}`;
  if (!shouldEmit(key, 10000)) return;
  fire(
    'Lead',
    {
      content_name: contentName,
      value: Number(value) || 0,
      currency: CURRENCY,
    },
    eventID ? { eventID } : undefined,
  );
};

/**
 * Fire AFTER payment is confirmed. Pass the server-generated `eventID`
 * (from the success URL) so Meta deduplicates this browser Purchase event
 * with the CAPI Purchase event the backend sent from the Stripe webhook.
 *
 * Also stores a sessionStorage flag so a reload of the success page
 * doesn't refire the event in the browser.
 */
export const trackPurchase = ({ orderId, orderTotal, contentIds = [], eventID } = {}) => {
  if (!orderTotal) return;
  if (orderId) {
    try {
      const k = `fb_purchase_${orderId}`;
      if (window.sessionStorage.getItem(k)) return;
      window.sessionStorage.setItem(k, '1');
    } catch (_) {
      /* sessionStorage may be disabled — fall through and still fire */
    }
  }
  fire(
    'Purchase',
    {
      value: Number(orderTotal) || 0,
      currency: CURRENCY,
      content_ids: contentIds.map(String),
      content_type: 'product',
    },
    eventID ? { eventID } : undefined,
  );
};
